/**
 * McpToolsCatalog — accordion grouped by plane, listing every tool
 * Smplify's MCP gateway currently exposes.
 */
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { ScopeChip } from "./ScopeChip";
import type { McpToolDTO } from "@/types/edrApi";

export interface McpToolsCatalogProps {
  tools: McpToolDTO[];
}

const PLANE_LABEL: Record<string, string> = {
  edr: "EDR",
  lighthouse: "Lighthouse (AI Governance)",
  system: "System",
};

export function McpToolsCatalog({ tools }: McpToolsCatalogProps) {
  const groups = new Map<string, McpToolDTO[]>();
  for (const t of tools) {
    const key = (t.plane ?? "other").toLowerCase();
    const bucket = groups.get(key) ?? [];
    bucket.push(t);
    groups.set(key, bucket);
  }

  const planes = Array.from(groups.keys()).sort();

  return (
    <Accordion type="multiple" defaultValue={planes} className="w-full">
      {planes.map((plane) => {
        const items = groups.get(plane) ?? [];
        const sorted = [...items].sort((a, b) =>
          (a.name ?? "").localeCompare(b.name ?? ""),
        );
        return (
          <AccordionItem key={plane} value={plane} className="border-border">
            <AccordionTrigger className="text-sm">
              <span className="flex items-baseline gap-2">
                <span className="font-medium text-foreground">
                  {PLANE_LABEL[plane] ?? plane}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {items.length} tool{items.length === 1 ? "" : "s"}
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="divide-y divide-border">
                {sorted.map((t, i) => (
                  <li
                    key={t.name ?? i}
                    className="grid grid-cols-1 gap-2 py-2.5 md:grid-cols-[minmax(0,1fr)_auto] md:items-start"
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-foreground">{t.name}</div>
                      {t.description && (
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {t.description}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {t.scope && <ScopeChip scope={t.scope} />}
                        {t.inputSchemaRef && (
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {t.inputSchemaRef}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-[11px] tabular-nums text-muted-foreground">
                      {(t.callsLast24h ?? 0).toLocaleString()} calls / 24h
                    </div>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
